import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, filter, Subject, take, takeUntil } from 'rxjs';
import { AuthCognitoService } from '../../auth/auth-cognito.service';
import { ContactsViewModel } from '../../contacts/contacts.view-model';
import { OrganizationsComponent } from '../../contacts/organizations/organizations.component';
import { Organization, UpdateProfileRequest } from '../../contacts/user.service';
import { AddOrgUserDialogComponent } from '../add-org-user-dialog/add-org-user-dialog.component';
import {
  DisableUserConfirmData,
  DisableUserConfirmDialogComponent,
} from '../disable-user-confirm-dialog/disable-user-confirm-dialog.component';
import {
  EnableUserConfirmData,
  EnableUserConfirmDialogComponent,
} from '../enable-user-confirm-dialog/enable-user-confirm-dialog.component';
import { CreateOrgUserRequest, OrgUserListItem, ResetOrgUserPasswordRequest } from '../org-user.model';
import { OrgUserService } from '../org-user.service';
import {
  accountStatusBadgeVariant,
  accountStatusLabel,
  displayNameForList,
  showEmailSubtitle,
  usersHeader,
} from '../org-user.util';
import {
  EditOrgUserDialogComponent,
  EditOrgUserDialogResult,
} from '../edit-org-user-dialog/edit-org-user-dialog.component';
import { ResetOrgUserPasswordDialogComponent } from '../reset-org-user-password-dialog/reset-org-user-password-dialog.component';
import { DashboardSectionHeaderComponent } from 'src/app/notification/dashboard/dashboard-section-header/dashboard-section-header.component';
import { MaterialModule } from 'src/app/shared/material/material.module';
import { SkeletonComponent } from 'src/app/shared/skeleton/skeleton.component';

@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    MatSidenavModule,
    ReactiveFormsModule,
    DashboardSectionHeaderComponent,
    OrganizationsComponent,
    SkeletonComponent,
  ],
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.scss',
  host: {
    class: 'contact-list',
  },
  changeDetection: ChangeDetectionStrategy.Default,
})
export class UsersListComponent implements OnInit, OnDestroy {
  #snackBar = inject(MatSnackBar);
  #dialog = inject(MatDialog);
  #authCognitoService = inject(AuthCognitoService);
  #orgUserService = inject(OrgUserService);
  #contactsViewModel = inject(ContactsViewModel);

  searchForm = new FormGroup({
    search: new FormControl<string>('', []),
  });

  #allUsers = signal<OrgUserListItem[]>([]);
  #searchTerm = signal('');
  usersLoading = signal(true);

  filteredUsers = computed(() => {
    const query = this.#searchTerm().trim().toLowerCase();
    const users = this.#allUsers();
    if (!query) {
      return users;
    }
    return users.filter((user) => {
      const q = query;
      if (user.email.toLowerCase().includes(q)) {
        return true;
      }
      return (user.name?.toLowerCase().includes(q) ?? false);
    });
  });

  usersCount = computed(() => this.#allUsers().length);

  locationsLoading = toSignal(this.#contactsViewModel.locationsLoading$);
  locations = toSignal(this.#contactsViewModel.locations$, { initialValue: [] });
  profile$ = this.#contactsViewModel.profile$;
  profileError$ = this.#contactsViewModel.profileError$;
  profile = toSignal(this.profile$);
  currentOrganization = toSignal(this.#contactsViewModel.currentOrganization$);

  @ViewChild('drawer', { static: true })
  organizationMenu!: MatSidenav;

  destroy$ = new Subject<void>();

  accountStatusLabel = accountStatusLabel;
  accountStatusBadgeVariant = accountStatusBadgeVariant;
  displayNameForList = displayNameForList;
  showEmailSubtitle = showEmailSubtitle;

  usersHeaderText(): string {
    return usersHeader(this.usersCount(), this.usersLoading());
  }

  constructor() {
    this.searchForm.valueChanges.pipe(debounceTime(200)).subscribe((value) => {
      this.#searchTerm.set(value.search ?? '');
    });
  }

  ngOnInit(): void {
    this.profile$
      .pipe(
        filter((p) => !!p),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loadUsers();
        this.#contactsViewModel.loadLocations();
      });

    this.profile$
      .pipe(
        take(1),
        filter((profile) => !profile),
      )
      .subscribe(() => {
        this.#contactsViewModel.loadProfile();
      });

    this.profileError$
      .pipe(
        takeUntil(this.destroy$),
        filter((e) => !!e),
      )
      .subscribe(() => {
        const ref = this.#snackBar.open("Sorry, we couldn't load the user information.", 'Okay', {
          duration: 5000,
        });
        ref.afterDismissed().subscribe(() => {
          this.onLogout();
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUsers(): void {
    this.usersLoading.set(true);
    this.#orgUserService.listUsers().subscribe({
      next: (users) => {
        this.#allUsers.set(users);
        this.usersLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading organization users', err);
        this.usersLoading.set(false);
        this.#snackBar.open('Could not load users. Please try again.', 'Okay', { duration: 5000 });
      },
    });
  }

  onOrganizationSelected(org: Organization): void {
    this.organizationMenu.toggle();
    if (org.id !== this.profile()?.currentOrg) {
      const args: UpdateProfileRequest = {
        name: this.profile()?.name || '',
        currentOrg: org.id,
      };
      this.#contactsViewModel.updateOrganization(args, org);
    }
  }

  onLogout(): void {
    this.#authCognitoService.signOut().then(() => {
      sessionStorage.removeItem('patientId');
      sessionStorage.removeItem('organizationId');
    });
  }

  onAddUser(): void {
    const ref = this.#dialog.open(AddOrgUserDialogComponent);
    ref.afterClosed().subscribe((result: CreateOrgUserRequest | undefined) => {
      if (!result) {
        return;
      }

      this.#orgUserService.createUser(result).subscribe({
        next: () => {
          this.loadUsers();
          this.#snackBar.open(
            'User created. Share the temporary password with them to sign in.',
            'Okay',
            { duration: 8000 },
          );
        },
        error: (err) => {
          console.error('Error creating organization user', err);
          this.#snackBar.open(
            'Could not add user. Check the email and password policy, then try again.',
            'Okay',
            { duration: 5000 },
          );
        },
      });
    });
  }

  canEditUser(user: OrgUserListItem): boolean {
    return user.enabled;
  }

  isSelf(user: OrgUserListItem): boolean {
    const self = this.#authCognitoService.getUserId().trim().toLowerCase();
    if (!self) {
      return false;
    }
    return (
      user.email.trim().toLowerCase() === self || user.username.trim().toLowerCase() === self
    );
  }

  canResetPassword(user: OrgUserListItem): boolean {
    return user.enabled;
  }

  canEnableUser(user: OrgUserListItem): boolean {
    return !user.enabled;
  }

  canDisableUser(user: OrgUserListItem): boolean {
    if (!user.enabled) {
      return false;
    }
    const self = this.#authCognitoService.getUserId().trim().toLowerCase();
    if (!self) {
      return true;
    }
    return (
      user.email.trim().toLowerCase() !== self && user.username.trim().toLowerCase() !== self
    );
  }

  onEditUser(user: OrgUserListItem): void {
    if (!this.canEditUser(user)) {
      return;
    }

    const initialName = user.name?.trim() || (this.isSelf(user) ? this.profile()?.name : undefined);

    const ref = this.#dialog.open(EditOrgUserDialogComponent, {
      data: {
        user,
        statusLabel: this.accountStatusLabel(user),
        initialName,
      },
    });

    ref.afterClosed().subscribe((result: EditOrgUserDialogResult | undefined) => {
      if (!result) {
        return;
      }

      this.#orgUserService.updateUser(user.email, { name: result.name }).subscribe({
        next: () => {
          this.loadUsers();
          if (this.isSelf(user)) {
            this.#contactsViewModel.loadProfile();
          }
          this.#snackBar.open('Display name saved.', 'Okay', { duration: 5000 });
        },
        error: (err) => {
          console.error('Error updating organization user display name', err);
          this.#snackBar.open(
            'Could not save display name. Saving will work once the server update is deployed.',
            'Okay',
            { duration: 7000 },
          );
        },
      });
    });
  }

  onResetPassword(user: OrgUserListItem): void {
    if (!this.canResetPassword(user)) {
      return;
    }

    const ref = this.#dialog.open(ResetOrgUserPasswordDialogComponent, {
      data: { email: user.email },
    });

    ref.afterClosed().subscribe((result: ResetOrgUserPasswordRequest | undefined) => {
      if (!result) {
        return;
      }

      this.#orgUserService.resetPassword(user.email, result).subscribe({
        next: () => {
          this.loadUsers();
          this.#snackBar.open(
            'Password reset. Share the new temporary password with the user.',
            'Okay',
            { duration: 8000 },
          );
        },
        error: (err) => {
          console.error('Error resetting organization user password', err);
          this.#snackBar.open(
            'Could not reset password. Check the password policy, then try again.',
            'Okay',
            { duration: 5000 },
          );
        },
      });
    });
  }

  onEnableUser(user: OrgUserListItem): void {
    const ref = this.#dialog.open(EnableUserConfirmDialogComponent, {
      data: { email: user.email } satisfies EnableUserConfirmData,
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.#orgUserService.enableUser(user.email).subscribe({
        next: () => {
          this.loadUsers();
          this.#snackBar.open('User enabled.', 'Okay', { duration: 5000 });
        },
        error: (err) => {
          console.error('Error enabling organization user', err);
          this.#snackBar.open('Could not enable user. Please try again.', 'Okay', {
            duration: 5000,
          });
        },
      });
    });
  }

  onDisableUser(user: OrgUserListItem): void {
    const ref = this.#dialog.open(DisableUserConfirmDialogComponent, {
      data: { email: user.email } satisfies DisableUserConfirmData,
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.#orgUserService.disableUser(user.email).subscribe({
        next: () => {
          this.loadUsers();
          this.#snackBar.open('User disabled.', 'Okay', { duration: 5000 });
        },
        error: (err) => {
          console.error('Error disabling organization user', err);
          this.#snackBar.open(
            'Could not disable user. You may not disable your own account or the last active user.',
            'Okay',
            { duration: 6000 },
          );
        },
      });
    });
  }

  clearSearch(searchInput: HTMLInputElement): void {
    this.searchForm.patchValue({ search: '' });
    searchInput.focus();
  }
}
